import { useState } from "react";
import clsx from "clsx";
import { zodResolver } from "@hookform/resolvers/zod";
import { SquarePenIcon, StarIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import { useCreateCandidacyScoreChange } from "@/hooks/mutations";
import {
  useRefetchCandidacies,
  useRefetchCandidacyScoreChanges,
} from "@/hooks/refetches";
import { candidacyScoreOptions } from "@/lib/constants";

const jobPipelineCandidateSheetCandidacyScoreDropdownSchema = z.object({
  score: z.string().min(1, { message: "Required" }),
});

export const JobPipelineCandidateSheetCandidacyScoreDropdown = ({
  candidacyId,
  candidacyScore,
}: {
  candidacyId: string;
  candidacyScore: number | null;
}) => {
  const [open, setOpen] = useState(false);
  const { mutate: createCandidacyScoreChange } =
    useCreateCandidacyScoreChange();
  const refetchCandidacies = useRefetchCandidacies();
  const refetchCandidacyScoreChanges = useRefetchCandidacyScoreChanges();

  const form = useForm<
    z.infer<typeof jobPipelineCandidateSheetCandidacyScoreDropdownSchema>
  >({
    defaultValues: {
      score: candidacyScore ? candidacyScore.toLocaleString() : "",
    },
    resolver: zodResolver(
      jobPipelineCandidateSheetCandidacyScoreDropdownSchema,
    ),
  });

  const handleSubmit = (
    data: z.infer<typeof jobPipelineCandidateSheetCandidacyScoreDropdownSchema>,
  ) => {
    if (candidacyScore && candidacyScore.toLocaleString() === data.score) {
      setOpen(false);
      return;
    }

    createCandidacyScoreChange(
      {
        candidacyId,
        createCandidacyScoreChangeRequest: {
          toScore: parseInt(data.score),
        },
      },
      {
        onSuccess: () => {
          setOpen(false);
          refetchCandidacies();
          refetchCandidacyScoreChanges({ candidacyId });
        },
      },
    );
  };

  return (
    <DropdownMenu onOpenChange={setOpen} open={open}>
      <DropdownMenuTrigger asChild>
        <Button
          className="h-auto rounded-full px-3 py-1"
          variant={candidacyScore ? "yellow" : "outlineSecondary"}
        >
          <StarIcon
            className={clsx(
              "h-4 w-4",
              candidacyScore
                ? "fill-yellow-500 text-yellow-500"
                : "fill-slate-300 text-slate-300",
            )}
          />
          {candidacyScore ?? "-"}/5
          <SquarePenIcon className="h-4 w-4 text-slate-700" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[250px] p-0">
        <Form {...form}>
          <form
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="score"
              render={({ field }) => (
                <FormItem className="p-4 text-slate-700">
                  <FormLabel>Score candidate</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex flex-col space-y-1"
                    >
                      {candidacyScoreOptions.map((candidacyScoreOption) => (
                        <FormItem
                          key={candidacyScoreOption.value}
                          className="flex gap-3 space-y-0"
                        >
                          <FormControl>
                            <RadioGroupItem
                              value={candidacyScoreOption.value}
                            />
                          </FormControl>
                          <FormLabel className="font-normal">
                            {candidacyScoreOption.label}
                          </FormLabel>
                        </FormItem>
                      ))}
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="flex justify-end border-t border-slate-200 p-2">
              <Button variant="ghost" type="submit">
                Apply
              </Button>
            </div>
          </form>
        </Form>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
